/* ============================================================================
   MODE « SCÈNE DIRECTE » — étape 3 de la bascule d'architecture
   ----------------------------------------------------------------------------
   L'IA produit la scène COMPLÈTE (prompt SCENE_PROMPT, Edge Function mode
   "scene") ; le déterminisme devient JUGE : la scène n'est servie que si les
   bancs n'y détectent AUCUN défaut grave (fidélité + règlement). Sinon : une
   réparation ciblée, puis repli automatique sur le pipeline classique.
   Mesure sur le lot doré (682 prompts des salves) : ce montage donne 591/682
   scènes à zéro défaut (87 %) et ZÉRO cas grave par construction — mieux que
   chaque chemin isolé (A seul 554, B seul 571). Cf. docs/salves-tests-deepseek.md.
   La lisibilité ne bloque PAS (elle n'est jamais une contradiction du texte) :
   elle reste mesurée par son banc, comme pour le pipeline classique.
   ========================================================================== */
import { fidelityIssues } from "./fidelity.js";
import { regulationIssues } from "./regulation.js";
import { legibilityIssues } from "./legibility.js";

/* Une réponse « scène » exploitable : des joueurs positionnés et un tableau
   d'actions (éventuellement vide : un schéma de match peut être statique). */
export function isValidSceneResult(r) {
  return !!r && Array.isArray(r.players) && r.players.length > 0
    && Array.isArray(r.actions)
    && r.players.every((p) => p && p.id != null && Number.isFinite(+p.x) && Number.isFinite(+p.y));
}

/* La GARDE. Renvoie { accept, grave, soft } :
   - `grave` (fidélité + règlement) DÉCIDE : accept ⇔ aucun défaut grave ;
   - `soft` (lisibilité) ne bloque jamais mais DÉCLENCHE la réparation et
     nourrit son message — c'est la configuration MESURÉE sur le lot doré
     (réparer sur tout défaut, accepter sur les seuls graves : 591/682).
   Les messages servent tels quels à la réparation ciblée (l'IA les lit). */
export function sceneGate(scene, text, sport) {
  const grave = [], soft = [];
  try { for (const i of fidelityIssues(scene, text, sport)) grave.push(`[fidélité] ${i.msg || i}`); }
  catch (_) { grave.push("[fidélité] scène inanalysable"); }
  try { for (const i of regulationIssues(scene, sport, text)) grave.push(`[règlement] ${i.msg}`); }
  catch (_) { grave.push("[règlement] scène inanalysable"); }
  try { for (const i of legibilityIssues(scene, sport)) soft.push(`[lisibilité] ${i.msg}`); }
  catch (_) { /* la lisibilité ne bloque jamais */ }
  return { accept: grave.length === 0, grave, soft };
}

/* La scène réparée remplace l'originale si elle fait STRICTEMENT mieux, aux
   MÊMES POIDS que le meilleur-des-deux ci-dessous (grave 10, soft 1) : un
   grave corrigé n'est jamais annulé par un soft apparu. L'ancien comptage à
   poids égaux jetait une réparation qui passait de {1 grave} à {1 soft} —
   c'est-à-dire de REJETÉE à ACCEPTÉE : l'utilisateur attendait la réparation
   et partait quand même au repli (salve handball nº2, H20 — la course d'élan
   prescrite était là, la scène était conforme, elle a été écartée pour 1 = 1).
   À égalité pondérée on garde la première — pas de loterie. */
export function repairIsBetter(before, after) {
  const w = (g) => g.grave.length * 10 + g.soft.length;
  return w(after) < w(before);
}

/* QUAND réparer, et COMMENT (retours prod 👎 « beaucoup trop long ») :
   - défaut GRAVE (fidélité/règlement)  -> réparation EN PREMIER PLAN : elle
     peut faire basculer le verdict rejet -> accepté, l'attente est le prix
     d'un schéma juste ;
   - défauts SOFT uniquement (lisibilité) -> la scène est DÉJÀ acceptée : elle
     est servie IMMÉDIATEMENT et la réparation devient un POLISSAGE EN
     ARRIÈRE-PLAN (l'intention était documentée de longue date par l'étiquette
     `…+repair+soft` : « scènes déjà acceptables, candidates à un polissage en
     arrière-plan »). Mesure salve nº1 : 14 réparations sur 17 étaient
     soft-only — soit ~36 s d'attente évitée sur l'écrasante majorité des
     réparations, sans rien perdre (le polissage arrive quand il arrive) ;
   - aucun défaut -> rien. */
export function repairMode(gate) {
  if (!gate || (gate.grave.length + gate.soft.length) === 0) return "none";
  return gate.grave.length > 0 ? "foreground" : "background";
}

/* MEILLEUR-DES-DEUX — l'Edge (mode scène, sans raisonnement) renvoie DEUX
   scènes tirées en parallèle (t° 0.2 et 0.7) ; la garde départage ICI, où
   vivent les juges. Un défaut GRAVE pèse 10, un soft 1 : on préfère toujours
   la scène la plus conforme, la lisibilité n'arbitre qu'à conformité égale.
   À égalité stricte, la première (t° basse, plus sage) l'emporte — pas de
   loterie. Renvoie { scene, gate } de la gagnante, ou null si aucune scène
   n'est exploitable. */
export function pickBestScene(candidates, text, sport) {
  const scored = (Array.isArray(candidates) ? candidates : [])
    .filter(isValidSceneResult)
    .map((scene) => ({ scene, gate: sceneGate(scene, text, sport) }));
  if (!scored.length) return null;
  const cost = (g) => g.grave.length * 10 + g.soft.length;
  let best = scored[0];
  for (const c of scored.slice(1)) if (cost(c.gate) < cost(best.gate)) best = c;
  return best;
}
